import type { Root } from "mdast";
import { Extension } from "mdast-util-from-markdown";
import {
  type Options as MatterOptions,
  frontmatterFromMarkdown,
} from "mdast-util-frontmatter";
import { frontmatter } from "micromark-extension-frontmatter";
import { defaultSchema } from "rehype-sanitize";
import { css } from "styled-components";
import type { Plugin } from "unified";

export type MarkdownFrontmatterDisplayOptions = MatterOptions;

const HEX_FRONTMATTER_CLASSNAME = "hex-frontmatter";

/**
 * Remark plugin that both parses frontmatter and renders it as
 * visible greyed-out text instead of hiding it.
 */
export const markdownFrontmatterDisplay: Plugin<
  [MarkdownFrontmatterDisplayOptions?],
  Root
> = function (this, options) {
  const data = this.data();
  ((data.micromarkExtensions ||= []) as Extension[]).push(frontmatter(options));
  ((data.fromMarkdownExtensions ||= []) as Extension[]).push(
    frontmatterFromMarkdown(options),
  );

  return (tree: Root) => {
    const firstNode = tree.children[0];
    if (firstNode?.type !== "yaml") {
      return;
    }

    tree.children[0] = {
      type: "paragraph",
      children: [{ type: "text", value: firstNode.value }],
      data: {
        hName: "div",
        hProperties: { className: [HEX_FRONTMATTER_CLASSNAME] },
      },
    };
  };
};

export const markdownFrontmatterDisplayStyles = css`
  .${HEX_FRONTMATTER_CLASSNAME} {
    color: ${({ theme }) => theme.fontColor.MUTED};
    font-family: ${({ theme }) => theme.fontFamily.MONO};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    line-height: ${({ theme }) => theme.lineHeight.SMALL};
    white-space: pre-wrap;
  }
`;

/**
 * Allow Hex's custom frontmatter class on div elements so that the plugin's
 * output survives sanitization.
 */
export const patchSanitizeSchemaForFrontmatter =
  (patchAttributeValues: PatchAttributeValues): PatchSchema =>
  (schema) => {
    return {
      ...schema,
      attributes: {
        ...schema.attributes,
        div: patchAttributeValues(
          (schema.attributes != null ? schema.attributes.div : undefined) ?? [],
          [["className", HEX_FRONTMATTER_CLASSNAME]],
        ),
      },
    };
  };

type Schema = typeof defaultSchema;
type PatchSchema = (schema: Schema) => Schema;

type AttributeValue = Required<Schema>["attributes"][string][number];
type PatchAttributeValues = (
  oldAttributeValues: AttributeValue[],
  newAttributeValues: AttributeValue[],
) => AttributeValue[];
